import moment from 'moment';
import { machineStatuses } from '@components/wallboard/components/functions';
import type { TimelineEvent, TimeWidths, TimeAccumulationResult } from '@components/wallboard/types';

export const calculateTimeWidths = (configWidth: number): TimeWidths => {
  const lineWidth = configWidth - 64;
  const hourWidth = lineWidth / 24;
  const minWidth = hourWidth / 60;
  const secondWidth = minWidth / 60;
  return { lineWidth, hourWidth, minWidth, secondWidth };
};

export const formatEventsForTimeline = (
  events: TimelineEvent[],
  localTimeFn: (...args: any[]) => any,
  wallboardSelectedLevel: string,
  wallboardTimelineEndDate: string,
  isSameDayFn: (date: string) => boolean
): TimelineEvent[] => {
  if (wallboardSelectedLevel !== 'today' || !isSameDayFn(wallboardTimelineEndDate)) {
    return events;
  }

  const startToday = localTimeFn().startOf('day');
  const formattedEvents: TimelineEvent[] = [];

  events.forEach((event) => {
    if (localTimeFn(event.startedOn).isBefore(startToday)) {
      if (localTimeFn(event.endedOn).isSameOrAfter(startToday)) {
        const startedOn = startToday.format('YYYY-MM-DDTHH:mm:ss.000Z');
        const durationInSeconds = moment.utc(event.endedOn).diff(moment.utc(startedOn), 'seconds');
        formattedEvents.push({ ...event, startedOn, durationInSeconds });
      }
    } else {
      formattedEvents.push(event);
    }
  });

  return formattedEvents;
};

export const accumulateTimeByStatus = (
  formattedEvents: TimelineEvent[],
  currentStatus: string,
  isSameDayResult: boolean,
  nowDiffSeconds?: number
): TimeAccumulationResult => {
  const timeEventsStorage: Record<string, number> = {
    [machineStatuses.ACTIVE]: 0,
    [machineStatuses.CHANGE]: 0,
    [machineStatuses.SETUP]: 0,
    [machineStatuses.NOTPRODUCTIVE]: 0,
  };

  formattedEvents.forEach((event) => {
    if (event.status in timeEventsStorage) {
      timeEventsStorage[event.status] += event.durationInSeconds;
    }
  });

  const lastEvent = formattedEvents[formattedEvents.length - 1];
  let highlightedStatus: string | null = null;

  if (lastEvent && isSameDayResult && nowDiffSeconds != null) {
    timeEventsStorage[currentStatus] += nowDiffSeconds;
    highlightedStatus = currentStatus;
  }

  return { timeEventsStorage, highlightedStatus };
};

export const buildTimelineSectionHtml = (props: {
  style: { width: number; height: string; backgroundColor: string };
  item: TimelineEvent;
  machineIndex: number;
  machineId: number;
  isWeb: boolean;
  isLast?: boolean;
}): string => {
  const { style, item, machineIndex, machineId, isWeb, isLast } = props;
  return `
  <div
  class="Wallboard-${item.status}"
  ${isWeb ? 'onMouseOver' : 'onClick'}='onMouseOver(this, "show", ${JSON.stringify({ ...item, machineIndex, machineId })})'
  onMouseOut='onMouseOver(this, "hide")'>
    <div style="width:${style.width}px;height:${style.height};background-color:${style.backgroundColor};" class="${isLast ? 'last-item' : ''}"></div>
  </div>`;
};

export const buildTimelineContentHtml = (
  formattedEvents: TimelineEvent[],
  secondWidth: number,
  currentStatus: string,
  colorFn: (status: string) => string,
  renderProps: { machineIndex: number; machineId: number; isWeb: boolean },
  isSameDayResult: boolean,
  nowUtcMs: number = Date.now()
): string => {
  let lineContent = '';
  const now = moment.utc(nowUtcMs);

  formattedEvents.forEach((event, index) => {
    const isLast = isSameDayResult && formattedEvents.length - 1 === index;

    if (isLast && event.status !== currentStatus) {
      lineContent += buildTimelineSectionHtml({
        item: event,
        style: {
          width: event.durationInSeconds * secondWidth,
          height: '100%',
          backgroundColor: colorFn(event.status),
        },
        ...renderProps,
      });
      lineContent += buildTimelineSectionHtml({
        item: event,
        isLast: true,
        style: {
          width: now.diff(moment.utc(event.endedOn), 'seconds') * secondWidth,
          height: '100%',
          backgroundColor: colorFn(currentStatus),
        },
        ...renderProps,
      });
      return;
    }

    const duration = isLast ? now.diff(moment.utc(event.startedOn), 'seconds') : event.durationInSeconds;
    lineContent += buildTimelineSectionHtml({
      item: event,
      isLast,
      style: {
        width: duration * secondWidth,
        height: '100%',
        backgroundColor: colorFn(event.status),
      },
      ...renderProps,
    });
  });

  return lineContent;
};

export const parseWebViewMessage = (rawData: string): { type: string; item?: any; width?: number; position?: any } | null => {
  try {
    const data = JSON.parse(rawData);
    if (data && typeof data === 'object' && typeof data.type === 'string') {
      return data;
    }
    return null;
  } catch {
    return null;
  }
};
